// packages import
import { Autocomplete, TextField } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

// others
import { UserRole, useFetchAllUsersLazyQuery, useSearchUserLazyQuery, User, Maybe } from '../../../generated'
import { GRAPHQL_QUERY_POLICY, GRAPHQL_QUERY_POLICY_CACHE_FIRST, PAGE_LIMIT } from '../../constants'

interface AttorneyDropdownProps {
  selectedAttorneyId?: string
  onChange: (value: Maybe<User>) => void
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  sx?: object
  roles?: UserRole[]
  /** Matches MUI's Autocomplete sizing, so this can sit inline with `size="small"` selects. */
  size?: 'small' | 'medium'
}

const AttorneyDropdown: FC<AttorneyDropdownProps> = ({
  selectedAttorneyId,
  onChange,
  label = 'Attorney',
  placeholder,
  required = false,
  disabled = false,
  sx = { minWidth: 150 },
  roles = [UserRole.Attorney],
  size = 'medium'
}) => {
  const [attorneys, setAttorneys] = useState<Maybe<User>[]>([])
  const [attorneyPage, setAttorneyPage] = useState<number>(1)
  const [attorneySearchTerm, setAttorneySearchTerm] = useState<string>('')
  // The attorney the user actually picked, held independently of the loaded page.
  const [pickedAttorney, setPickedAttorney] = useState<Maybe<User>>(null)

  // Resolve the displayed option from the loaded page first, then fall back to the
  // pinned pick. Options load PAGE_LIMIT (10) at a time out of ~240 users and are
  // wiped on every search, so an attorney chosen from search results — or sitting on
  // page 2+ — is routinely absent from `attorneys`. Looking only there made the field
  // go blank moments after picking, which read as "the dropdown won't select them".
  // The id comparison keeps this honest: when the caller clears its state, the pin is
  // ignored and the field clears too.
  const loadedMatch = attorneys.find(attorney => attorney?.id === selectedAttorneyId)
  const value = loadedMatch ?? (pickedAttorney?.id === selectedAttorneyId ? pickedAttorney : null)

  // MUI warns when `value` is not among `options`; surface the pinned pick so the
  // selected attorney is always a legitimate option.
  const options = value && !loadedMatch ? [value, ...attorneys] : attorneys

  const [debouncedAttorneySearchTerm] = useDebounce(attorneySearchTerm, 500)

  const [fetchAttorneys, { loading: attorneyLoading }] = useFetchAllUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onError() {},

    onCompleted(data) {
      if (data) {
        const { fetchAllUsers } = data
        if (fetchAllUsers) {
          const { users } = fetchAllUsers
          setAttorneys(prevAttorneys => (prevAttorneys ? [...prevAttorneys, ...(users || [])] : users || []))
        }
      }
    }
  })

  const [searchAttorneys, { loading: attorneySearchLoading }] = useSearchUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},

    onCompleted(data) {
      if (data?.searchUser?.users && attorneySearchTerm) {
        setAttorneys(data.searchUser.users || [])
      }
    }
  })

  const handleAttorneyChange = (newValue: Maybe<User>) => {
    setPickedAttorney(newValue)
    onChange(newValue)
  }

  useEffect(() => {
    if (!debouncedAttorneySearchTerm) {
      fetchAttorneys({
        variables: {
          userInput: {
            paginationOptions: { page: attorneyPage, limit: PAGE_LIMIT },
            roles
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attorneyPage, debouncedAttorneySearchTerm, fetchAttorneys])

  useEffect(() => {
    setAttorneys([])
    setAttorneyPage(1)

    if (debouncedAttorneySearchTerm) {
      searchAttorneys({
        variables: {
          searchUserInput: {
            roles,
            searchTerm: debouncedAttorneySearchTerm
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAttorneySearchTerm, searchAttorneys])

  return (
    <Autocomplete
      sx={sx}
      size={size}
      options={options}
      // `?? null` keeps this a CONTROLLED input. `value` is undefined whenever the
      // selected id is cleared or its option is not in the loaded page, and MUI
      // treats an undefined value as uncontrolled — it then keeps rendering the
      // previously picked attorney even though the caller's state is empty.
      value={value ?? null}
      onChange={(_, newValue) => handleAttorneyChange(newValue)}
      // Only genuine typing drives a search. MUI also fires this with reason 'reset'
      // when an option is picked (input text becomes the option label) and 'clear' on
      // the X — both used to set the search term, which then wiped the options list
      // and blanked the field the user had just filled in.
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') setAttorneySearchTerm(newInputValue)
      }}
      getOptionLabel={option => `${option?.firstName || ''} ${option?.lastName || ''}`.trim()}
      loading={attorneyLoading || attorneySearchLoading}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      disabled={disabled}
      renderInput={params => <TextField {...params} label={label} placeholder={placeholder} required={required} />}
      ListboxProps={{
        onScroll: event => {
          const listboxNode = event.currentTarget
          if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
            setAttorneyPage(attorneyPage + 1)
          }
        }
      }}
    />
  )
}

export default AttorneyDropdown
